import {
  getOrganizationBearerHeaders,
  getRouteOrganizationBearerHeaders,
} from "@/utils/auth/organization-token"

const ORGANIZATION_MISMATCH_MESSAGE =
  "Sofia could not confirm which organization this tab belongs to. Reload the page and try again."

interface SofiaProxyRequestInit extends Omit<RequestInit, "headers"> {
  headers?: Record<string, string>
  organizationSlug?: string
}

/**
 * Bearer credentials for one `/api/sofia/*` request, scoped to the organization
 * this tab is showing.
 *
 * The proxy routes resolve the caller through `getAuth(req)`, which falls back to
 * the browser-global `__session` cookie when a request carries no `Authorization`
 * header. That cookie holds one organization per browser profile and is rewritten
 * by whichever tab last activated one, so a turn sent from a background tab is
 * forwarded upstream under a different organization — and Sofia executes actions.
 * Clerk prefers a header token over the cookie, so minting one pins the request
 * to the tab that sent it.
 *
 * The organization named in the tab's URL wins, with its id resolved through the
 * session's own memberships so the mint succeeds even while another tab holds a
 * different organization active. On a route that names none — global Sofia Home
 * at `/home` still chats — the mint falls back to Clerk's active organization,
 * which is what the panel's own state follows there. Yields no header outside
 * the browser, without a Clerk session, or when neither signal names an
 * organization; when a session exists but the mint cannot produce a token for
 * that organization, the request fails rather than going out under the cookie's
 * organization.
 */
async function getSofiaAuthorizationHeaders(
  skipCache = false,
  organizationSlug?: string,
): Promise<Record<string, string>> {
  try {
    return organizationSlug
      ? await getOrganizationBearerHeaders(organizationSlug, { skipCache })
      : await getRouteOrganizationBearerHeaders({ skipCache })
  } catch (error: unknown) {
    throw new Error(ORGANIZATION_MISMATCH_MESSAGE, { cause: error })
  }
}

/** `fetch` for the Sofia proxy routes, authenticated as this tab's organization. */
export async function fetchSofiaProxy(
  path: string,
  init: SofiaProxyRequestInit = {},
): Promise<Response> {
  const { headers, organizationSlug, ...requestInit } = init
  const send = async (skipCache: boolean) =>
    fetch(path, {
      ...requestInit,
      headers: {
        ...headers,
        ...(await getSofiaAuthorizationHeaders(skipCache, organizationSlug)),
      },
    })

  const response = await send(false)
  if (response.status !== 401) {
    return response
  }
  await response.body?.cancel().catch(() => undefined)
  return send(true)
}
